// Shared date utility — always use LOCAL date, never UTC-based toISOString (shifts ±1 near midnight in non-UTC zones).
export function localYMD(date = new Date()) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export function today() { return localYMD(new Date()); }

// HH:MM for the current local time
export function currentHHMM() {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Convert "YYYY-MM-DD" or any Date-parseable input → "DD/MM/YYYY".
 * Falls back to the original string if parsing fails (so we never display NaN/NaN).
 * Mod 4 — used everywhere a date is rendered to the user.
 */
export function ddmmyyyy(input) {
    if (!input) return "";
    // Fast path for ISO YYYY-MM-DD (avoid TZ shifts)
    if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}/.test(input)) {
        const [y, m, d] = input.slice(0, 10).split("-");
        return `${d}/${m}/${y}`;
    }
    try {
        const d = new Date(input);
        if (isNaN(d.getTime())) return String(input);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        return `${dd}/${mm}/${d.getFullYear()}`;
    } catch {
        return String(input);
    }
}

/** Range helper: "01/05/2026 – 03/05/2026" or single date when start == end. */
export function ddmmyyyyRange(start, end) {
    const s = ddmmyyyy(start);
    const e = ddmmyyyy(end);
    if (!s) return e;
    if (!e || s === e) return s;
    return `${s} – ${e}`;
}

/**
 * Convert "HH:MM" (24-hour) → "h:mm AM/PM" (12-hour).
 * Iter 37 — used everywhere a slot/start/end time is shown to the user.
 * Robust to "24:00" / "0:00" / missing input.
 */
export function time12h(hhmm) {
    if (!hhmm || typeof hhmm !== "string") return "";
    const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return hhmm;
    let h = parseInt(m[1], 10);
    const mm = m[2];
    if (Number.isNaN(h)) return hhmm;
    // "24:00" → "12:00 AM" (next day midnight, but display as midnight)
    if (h === 24) h = 0;
    const period = h >= 12 ? "PM" : "AM";
    let hr = h % 12;
    if (hr === 0) hr = 12;
    return `${hr}:${mm} ${period}`;
}

/** "HH:MM-HH:MM" or "HH:MM–HH:MM" range → "h:mm AM/PM – h:mm AM/PM". */
export function timeRange12h(range) {
    if (!range) return "";
    const parts = String(range).split(/[-–]/);
    if (parts.length !== 2) return range;
    return `${time12h(parts[0].trim())} – ${time12h(parts[1].trim())}`;
}

/** Iter 41 — Weekday label for booking summaries: "Mon", "Tue", …
 * Accepts "YYYY-MM-DD" or a Date. Returns "" for invalid input. */
export function weekdayShort(dateStr) {
    if (!dateStr) return "";
    try {
        const d = typeof dateStr === "string" ? new Date(dateStr + (dateStr.length === 10 ? "T00:00:00" : "")) : new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-US", { weekday: "short" });
    } catch { return ""; }
}

/** Iter 41 — Full weekday label: "Monday", "Tuesday", … */
export function weekdayLong(dateStr) {
    if (!dateStr) return "";
    try {
        const d = typeof dateStr === "string" ? new Date(dateStr + (dateStr.length === 10 ? "T00:00:00" : "")) : new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-US", { weekday: "long" });
    } catch { return ""; }
}


